'use client';

import { createContext, ReactNode, useCallback, useContext, useEffect } from "react";
import {
    useCollectionTypeEditor,
    UseCollectionTypeEditorType
} from "@/app/lib/hooks/use-editor/useCollectionTypeEditor";
import { PartnerEditable } from "@/app/features/sites/cooperations/types/types";
import { useSiteCooperationsEditor } from "@/app/features/sites/cooperations/contexts/SiteCooperationsEditor";
import { Partner, PartnerInput, PublicationStatus } from "@/app/lib/generated/graphql-types";
import { PartnerSchema } from "@/app/features/sites/cooperations/types/schema";
import { updatePartner } from "@/app/features/sites/cooperations/data-access/mutations";
import { getPartner } from "@/app/features/sites/cooperations/data-access/queries";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import ErrorBox from "@/app/__to_be_integrated/_____components/_NEW/ErrorBox";
import { editableFromGraphQl } from "@/app/lib/hooks/use-editor/schema-mapper";

const PartnerEditorContext = createContext<UseCollectionTypeEditorType<PartnerEditable>| undefined>(undefined);

export function usePartnerEditor() {
    const context = useContext(PartnerEditorContext);
    if (!context) {
        throw new Error("usePartnerEditorContext must be used within a PartnerEditorProvider");
    }
    return context;
}

interface PartnerEditorProviderProps {
    documentId: string;
    children: ReactNode;
}

export default function PartnerEditorProvider({ documentId, children }: PartnerEditorProviderProps) {
    const { registerChildEditor, unregisterChildEditor } = useSiteCooperationsEditor();
    const toEditable = useCallback((partner: Partner) => editableFromGraphQl({
        ...partner,
        order: partner.order ?? -1,
    }, PartnerSchema), []);

    const editor = useCollectionTypeEditor<Partner, PartnerEditable, PartnerInput>({
        documentId,
        schema: PartnerSchema,
        loadEntity: getPartner,
        update: updatePartner,
        toEditable,
        autosave: async (targetDocumentId, input) => {
            return await updatePartner({
                documentId: targetDocumentId,
                data: input,
                status: PublicationStatus.Draft,
            });
        },
    })

    useEffect(() => {
        registerChildEditor(
            documentId,
            {
                hasChanges: editor.hasChanges,
                autosaveState: editor.autosaveState,
            },
            {
                publish: editor.publish,
                discardChanges: editor.discardChanges,
            }
        );

        return () => {
            unregisterChildEditor(documentId);
        };
    }, [
        documentId,
        editor.hasChanges,
        editor.autosaveState,
        editor.publish,
        editor.discardChanges,
        registerChildEditor,
        unregisterChildEditor,
    ]);

    if (editor.isLoading) return <LoadingSpinner />;
    if (!editor.editable) return <ErrorBox error={`Partner konnte nicht geladen werden${editor.error ? `: ${editor.error}` : '.'}`} />;


    return (
        <PartnerEditorContext.Provider value={editor}>
            {children}
        </PartnerEditorContext.Provider>
    )
}
